// 心智圖：橫向樹狀圖（中心主題在左，分支往右展開）；點節點可收合
// 資料為縮排大綱：每 2 個空白（或 1 個 Tab）一層，行首的「- 」「* 」可有可無
import React, { useMemo, useState } from 'react';

export type MapNode = { text: string; children: MapNode[] };

export function parseOutline(title: string, text: string): MapNode {
  const root: MapNode = { text: title, children: [] };
  const stack: { depth: number; node: MapNode }[] = [{ depth: -1, node: root }];
  text.split('\n').forEach(raw => {
    const line = raw.replace(/\t/g, '  ');
    const body = line.trim().replace(/^[-*•]\s+/, '');
    if (!body) return;
    const indent = line.length - line.trimStart().length;
    // 不可比上一行深超過一層（縮排打太多時視為下一層）
    const depth = Math.min(Math.floor(indent / 2), stack[stack.length - 1].depth + 1);
    while (stack[stack.length - 1].depth >= depth) stack.pop();
    const node: MapNode = { text: body, children: [] };
    stack[stack.length - 1].node.children.push(node);
    stack.push({ depth, node });
  });
  return root;
}

export const countNodes = (n: MapNode): number => 1 + n.children.reduce((a, c) => a + countNodes(c), 0);

// 朗讀用：只唸目前展開的節點
export function mapToSpeech(n: MapNode, isOpen: (path: string) => boolean, path = '0'): string {
  const kids = isOpen(path) ? n.children.map((c, i) => mapToSpeech(c, isOpen, `${path}.${i}`)) : [];
  return [n.text, ...kids].join('。\n');
}

const CSS = `
.mm-row{display:flex;align-items:center}
.mm-kids{display:flex;flex-direction:column;position:relative;margin-left:18px}
.mm-kids::before{content:'';position:absolute;left:-18px;top:50%;width:18px;border-top:2px solid var(--mm-line)}
.mm-item{position:relative;padding:3px 0 3px 14px;display:flex;align-items:center}
.mm-item::before{content:'';position:absolute;left:0;top:50%;width:14px;border-top:2px solid var(--mm-line)}
.mm-item::after{content:'';position:absolute;left:0;top:0;bottom:0;border-left:2px solid var(--mm-line)}
.mm-item:first-child::after{top:50%}
.mm-item:last-child::after{bottom:50%}
.mm-item:only-child::after{display:none}
.mm-node{border-radius:10px;padding:6px 10px;font-size:13px;line-height:1.45;text-align:left;max-width:190px;white-space:normal;word-break:break-word;flex-shrink:0;font-family:inherit}
`;

export default function MindMap({ title, text, color = 'var(--accent)', renderText, speak }: {
  title: string;
  text: string;
  color?: string;
  renderText?: (s: string) => React.ReactNode;
  speak?: (text: string) => React.ReactNode;   // 傳入朗讀按鈕（內容為目前展開的節點）
}) {
  const root = useMemo(() => parseOutline(title, text), [title, text]);
  // 預設只展開到第二層（中心主題 + 第一層分支）；toggled 記錄與預設相反的節點
  const [toggled, setToggled] = useState<Set<string>>(new Set());
  const [all, setAll] = useState<boolean | null>(null);   // 全部展開／收合後的基準；null 為預設
  const isOpen = (path: string) => {
    const base = all === null ? path === '0' : all || path === '0';
    return toggled.has(path) ? !base : base;
  };
  const toggle = (path: string) => setToggled(s => { const n = new Set(s); if (n.has(path)) n.delete(path); else n.add(path); return n; });

  const render = (n: MapNode, path: string, depth: number): React.ReactNode => {
    const open = isOpen(path), has = n.children.length > 0;
    const style: React.CSSProperties = depth === 0
      ? { background: color, color: '#fff', fontWeight: 700, fontSize: 15, border: 'none' }
      : depth === 1
        ? { background: `color-mix(in srgb, ${color} 14%, var(--card))`, color: 'var(--text)', fontWeight: 700, border: `1.5px solid ${color}` }
        : { background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)' };
    return (
      <div className="mm-row">
        <button className="mm-node" onClick={() => has && toggle(path)} aria-expanded={has ? open : undefined}
          style={{ ...style, cursor: has ? 'pointer' : 'default' }}>
          {renderText ? renderText(n.text) : n.text}
          {has && !open && <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.75, whiteSpace: 'nowrap' }}>＋{n.children.length}</span>}
        </button>
        {has && open && (
          <div className="mm-kids">
            {n.children.map((c, i) => <div className="mm-item" key={i}>{render(c, `${path}.${i}`, depth + 1)}</div>)}
          </div>
        )}
      </div>
    );
  };

  const ctl: React.CSSProperties = { padding: '5px 10px', borderRadius: 99, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
  return (
    <div style={{ background: 'var(--card)', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 6px rgba(0,0,0,.06)', ['--mm-line' as any]: `color-mix(in srgb, ${color} 45%, var(--border))` }}>
      <style>{CSS}</style>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <button style={ctl} onClick={() => { setAll(true); setToggled(new Set()); }}>全部展開</button>
        <button style={ctl} onClick={() => { setAll(false); setToggled(new Set()); }}>全部收合</button>
        <span style={{ flex: 1 }} />
        {speak?.(mapToSpeech(root, isOpen))}
      </div>
      <div style={{ overflowX: 'auto', paddingBottom: 6 }}>
        <div style={{ display: 'inline-block', minWidth: 'max-content' }}>{render(root, '0', 0)}</div>
      </div>
    </div>
  );
}
